import React, {useEffect, useState} from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import {useAuth} from '../auth/auth'
import { useSelector, useDispatch } from 'react-redux'
import { clearError } from '../store/slices/error'
import {
    ACCESS_DENIED,
    AUTH_DENIED,
    MISSING_AUTH,
    MISSING_TK_ASSIGNMENT,
    NOT_CLOCKED_IN,
    ROLE_MEMBERSHIP
} from './ApiError'
import ErrorIcon from '@mui/icons-material/Error'
import {Box} from '@mui/material'
import {useNavigate, useLocation} from 'react-router-dom'
import {RootState} from '../store'
import {ErrorState} from '../store/slices/error'

const NOT_CLOCKED_IN_MSG =
    'Please clock in to continue'
const ROLE_MEMBERSHIP_MSG =
    'There is an issue with your permissions. Please contact IT to update your setup'
const MISSING_TK_ASSIGNMENT_MSG =
    'You must be assigned to a truck to continue. Please contact your Regional Manager to be assigned'
const MISSING_AUTH_MSG = 'Missing Authorization Header. Please contact IT'
const ACCESS_DENIED_MSG = 'You don\'t have the right access. Please contact IT'
const AUTH_DENIED_MSG = 'Authorization is denied. Please contact IT'

const logoutRequiredErrors = new Set([
    ROLE_MEMBERSHIP,
    MISSING_TK_ASSIGNMENT,
    MISSING_AUTH,
    ACCESS_DENIED,
    AUTH_DENIED
])

const ErrorDialog = () => {
    const error = useSelector((state: RootState) => state.error)
    const {logout, clockIn, getUser} = useAuth()
    const [mustLogout, setMustLogout] = useState<boolean>(true)
    const [receivingError, setReceivingError] = useState<boolean>(true)
    const [mustClockIn, setMustClockIn] = useState(false)
    const [open, setOpen] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()

    const handleClose = () => {
        if (receivingError) {
            navigate('/inventory/receive')
        } else if (error.onClose) {
            error.onClose()
        } else if (mustLogout) {
            logout()
        }

        dispatch(clearError())
    }

    useEffect(() => {
        if (!error.type) {
            setOpen(false)
            return
        } else {
            setOpen(true)
        }
        setMustLogout(logoutRequiredErrors.has(error.type as string))
        setMustClockIn(error.type === NOT_CLOCKED_IN)
        setReceivingError(location.pathname.includes('inventory/receive'))
    }, [error, location.pathname])

    // Define error messages based on errorType
    const getErrorMessage = (error: ErrorState) => {
        switch (error.type) {
            case NOT_CLOCKED_IN:
                return NOT_CLOCKED_IN_MSG
            case ROLE_MEMBERSHIP:
                return ROLE_MEMBERSHIP_MSG
            case MISSING_TK_ASSIGNMENT:
                return MISSING_TK_ASSIGNMENT_MSG
            case MISSING_AUTH:
                return MISSING_AUTH_MSG
            case ACCESS_DENIED:
                return ACCESS_DENIED_MSG
            case AUTH_DENIED:
                return AUTH_DENIED_MSG
            default:
                return error.message
        }
    }

    const handleClockIn = async () => {
        await clockIn()
        navigate(location.pathname) // refresh the page
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableEscapeKeyDown
            //disableBackdropClick
            fullWidth
            maxWidth="sm"
            sx={{
                '& .MuiDialog-paper': {
                    padding: '18px'
                }
            }}>
            <DialogTitle>
                <Typography
                    component={'div'}
                    variant="h6"
                    style={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        textAlign: 'center'
                    }}>
                    <ErrorIcon
                        sx={{
                            color: 'error.main',
                            marginRight: '8px'
                        }}
                    />
                    Error: {error.type}
                </Typography>
            </DialogTitle>
            <DialogContent
                sx={{
                    textAlign: 'left',
                    width: '100%'
                }}>
                <Typography>{getErrorMessage(error)}</Typography>
            </DialogContent>
            <DialogActions>
                {mustLogout ? (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClose}>
                        Logout
                    </Button>
                ) : (
                    mustClockIn ? (
                        <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => logout()}>
                                Logout
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleClockIn}>
                                Clock In
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleClose}>
                            Close
                        </Button>
                    ))}
            </DialogActions>
        </Dialog>
    )
}

export default ErrorDialog
